import React, { useState, useEffect, useRef } from 'react';
import { jobAssignmentsAPI, jobsAPI } from '../utils/api';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, isSameDay, parseISO, isWithinInterval } from 'date-fns';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Calendar, Printer, Users } from 'lucide-react';

const ResourceCalendar = () => {
  const [assignments, setAssignments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 })); // Monday start
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    loadData();
  }, [currentWeekStart]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [assignmentsRes, jobsRes] = await Promise.all([
        jobAssignmentsAPI.getAll(),
        jobsAPI.getAll()
      ]);
      setAssignments(assignmentsRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      toast.error('Failed to load resource calendar data');
    } finally {
      setLoading(false);
    }
  };

  const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start: currentWeekStart, end: weekEnd });

  // Get assignments that overlap with a specific day
  const getAssignmentsForDay = (day) => {
    return assignments.filter(assignment => {
      if (assignment.status === 'cancelled') return false;
      const start = parseISO(assignment.assignment_start);
      const end = parseISO(assignment.assignment_end);
      return isWithinInterval(day, { start, end }) || isSameDay(day, start) || isSameDay(day, end);
    });
  };

  // Group assignments by job for a specific day
  const getJobGroupedAssignments = (day) => {
    const dayAssignments = getAssignmentsForDay(day);
    const jobGroups = {};

    dayAssignments.forEach(assignment => {
      const jobId = assignment.job_id;
      if (!jobGroups[jobId]) {
        const job = jobs.find(j => j.id === jobId);
        jobGroups[jobId] = {
          job_id: jobId,
          job_code: assignment.job_code || job?.job_code || '',
          job_title: assignment.job_title || job?.title || 'Unknown Job',
          resources: []
        };
      }
      jobGroups[jobId].resources.push({
        member_id: assignment.member_id,
        member_name: assignment.member_name,
        planned_hours: assignment.planned_hours_per_day || 8,
        status: assignment.status
      });
    });

    return Object.values(jobGroups);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Resource Calendar - Week of ${format(currentWeekStart, 'MMM dd, yyyy')}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .header h1 { font-size: 24px; color: #1e3a5f; }
            .header p { color: #666; margin-top: 5px; }
            .calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; }
            .day-column { border: 1px solid #e5e7eb; border-radius: 8px; min-height: 200px; }
            .day-header { background: #1e3a5f; color: white; padding: 10px; text-align: center; border-radius: 8px 8px 0 0; }
            .day-header .day-name { font-weight: bold; font-size: 14px; }
            .day-header .day-date { font-size: 12px; opacity: 0.9; }
            .day-content { padding: 8px; }
            .job-group { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; margin-bottom: 8px; padding: 8px; }
            .job-header { font-weight: bold; font-size: 11px; color: #1e3a5f; margin-bottom: 4px; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; }
            .job-code { color: #3b82f6; font-family: monospace; }
            .resource { font-size: 10px; padding: 3px 6px; background: #dbeafe; border-radius: 3px; margin: 2px 0; color: #1e40af; }
            .no-assignments { color: #9ca3af; font-size: 11px; text-align: center; padding: 20px; }
            @media print {
              body { padding: 10px; }
              .day-column { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="https://customer-assets.emergentagent.com/job_lbr-build/artifacts/t9f432h8_MB%20Con%20Logo.png" alt="MBConstruction" style="height: 80px; margin-bottom: 10px;" />
            <p>Resource Calendar - Week of ${format(currentWeekStart, 'MMMM dd, yyyy')} to ${format(weekEnd, 'MMMM dd, yyyy')}</p>
          </div>
          <div class="calendar-grid">
            ${weekDays.map(day => {
              const jobGroups = getJobGroupedAssignments(day);
              return `
                <div class="day-column">
                  <div class="day-header">
                    <div class="day-name">${format(day, 'EEE')}</div>
                    <div class="day-date">${format(day, 'MMM dd')}</div>
                  </div>
                  <div class="day-content">
                    ${jobGroups.length === 0 
                      ? '<div class="no-assignments">No assignments</div>'
                      : jobGroups.map(group => `
                        <div class="job-group">
                          <div class="job-header">
                            ${group.job_code ? `<span class="job-code">${group.job_code}</span> - ` : ''}
                            ${group.job_title}
                          </div>
                          ${group.resources.map(r => `
                            <div class="resource">${r.member_name}</div>
                          `).join('')}
                        </div>
                      `).join('')
                    }
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const goToPreviousWeek = () => {
    setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  };

  const goToCurrentWeek = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading resource calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="resource-calendar" ref={printRef}>
      {/* Header Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Week of {format(currentWeekStart, 'MMMM dd')} - {format(weekEnd, 'MMMM dd, yyyy')}
          </h2>
          <p className="text-slate-600 text-sm mt-1">Resources grouped by job per day</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            data-testid="print-resource-calendar-btn"
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 font-medium rounded-lg transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <button
            onClick={goToPreviousWeek}
            data-testid="prev-week-btn"
            className="p-2 border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goToCurrentWeek}
            className="px-4 py-2 bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium rounded-lg transition-colors"
          >
            This Week
          </button>
          <button
            onClick={goToNextWeek}
            data-testid="next-week-btn"
            className="p-2 border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-sm text-slate-600">Resource assigned to job</span>
        </div>
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-500" />
          <span className="text-sm text-slate-600">Team member count per job</span>
        </div>
      </div>

      {/* Weekly Calendar Grid */}
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
        <div className="grid grid-cols-7 divide-x divide-slate-200">
          {weekDays.map((day, idx) => {
            const isToday = isSameDay(day, new Date());
            const jobGroups = getJobGroupedAssignments(day);
            
            return (
              <div 
                key={idx} 
                data-testid={`resource-day-${format(day, 'yyyy-MM-dd')}`}
                className={`min-h-[400px] flex flex-col ${isToday ? 'bg-blue-50/30' : ''}`}
              >
                {/* Day Header */}
                <div className={`p-3 text-center border-b ${isToday ? 'bg-blue-600 text-white' : 'bg-slate-50'}`}>
                  <div className={`text-sm font-semibold ${isToday ? 'text-white' : 'text-slate-700'}`}>
                    {format(day, 'EEE')}
                  </div>
                  <div className={`text-lg font-bold ${isToday ? 'text-white' : 'text-slate-900'}`}>
                    {format(day, 'd')}
                  </div>
                  <div className={`text-xs ${isToday ? 'text-blue-100' : 'text-slate-500'}`}>
                    {format(day, 'MMM')}
                  </div>
                </div>

                {/* Day Content - Jobs grouped with resources */}
                <div className="flex-1 p-2 overflow-y-auto">
                  {jobGroups.length === 0 ? (
                    <div className="text-center text-slate-400 text-sm py-4">
                      No assignments
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {jobGroups.map((group, groupIdx) => (
                        <Link 
                          key={groupIdx} 
                          to={`/jobs/${group.job_id}`}
                          className="block"
                        >
                          <div 
                            data-testid={`job-group-${group.job_id}`}
                            className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-lg p-2 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                          >
                            {/* Job Header */}
                            <div className="flex items-start justify-between gap-1 mb-2 pb-1 border-b border-slate-200">
                              <div className="flex-1 min-w-0">
                                {group.job_code && (
                                  <span className="text-xs font-mono font-bold text-blue-600 block truncate">
                                    {group.job_code}
                                  </span>
                                )}
                                <span className="text-xs font-medium text-slate-700 block truncate" title={group.job_title}>
                                  {group.job_title}
                                </span>
                              </div>
                              <span className="flex items-center gap-1 text-xs text-slate-500 bg-white px-1.5 py-0.5 rounded">
                                <Users className="w-3 h-3" />
                                {group.resources.length}
                              </span>
                            </div>

                            {/* Resources List */}
                            <div className="space-y-1">
                              {group.resources.map((resource, resIdx) => (
                                <div 
                                  key={resIdx}
                                  data-testid={`resource-${resource.member_id}`}
                                  className="flex items-center justify-between bg-white rounded px-2 py-1 text-xs border border-slate-100"
                                >
                                  <span className="font-medium text-slate-700 truncate flex-1" title={resource.member_name}>
                                    {resource.member_name}
                                  </span>
                                  <span className="text-slate-400 ml-1 whitespace-nowrap">
                                    {resource.planned_hours}h
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-sm text-slate-600">Total Assignments This Week</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">
            {weekDays.reduce((total, day) => total + getAssignmentsForDay(day).length, 0)}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-sm text-slate-600">Unique Jobs This Week</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">
            {new Set(
              weekDays.flatMap(day => 
                getAssignmentsForDay(day).map(a => a.job_id)
              )
            ).size}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="text-sm text-slate-600">Unique Resources This Week</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">
            {new Set(
              weekDays.flatMap(day => 
                getAssignmentsForDay(day).map(a => a.member_id)
              )
            ).size}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceCalendar;
